import { jwtDecode } from "jwt-decode";
import { useMemo } from "react";
import useAuth from "src/hooks/useAuth";
import { Jwt } from "src/models/Auth";
import { LauncherApp } from "./types";

const CLAIM = "https://storefinancial.net/launcherApp";

/**
 * Parses the raw `launcherApp` claim value into a validated list of apps.
 *
 * Implements the normative parsing rules from the porting guide (§2):
 *  - the claim is either an array of strings, a single bare string, or absent;
 *  - each entry is a JSON string describing one app;
 *  - malformed entries are dropped, and this never throws — an absent or
 *    unparseable claim yields an empty list (which renders the empty state).
 *
 * Exported so the normative rules can be exercised in isolation.
 */
export const parseLauncherClaim = (claim: string | string[] | undefined): LauncherApp[] => {
  // Normalize to an array: array -> as-is, string -> [string], else -> [].
  // The single-claim collapse (a lone entry arriving as a bare string) is the
  // #1 parsing bug — handle both shapes or a user with exactly one other app
  // sees nothing.
  const entries = Array.isArray(claim) ? claim : typeof claim === "string" ? [claim] : [];

  const apps: LauncherApp[] = [];
  for (const entry of entries) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(entry);
    } catch {
      continue; // drop entries that fail to parse
    }

    if (!parsed || typeof parsed !== "object") continue;
    const { name, url, iconUrl } = parsed as Record<string, unknown>;

    // Validate: name/url non-empty strings, iconUrl a string. Drop failures.
    if (typeof name !== "string" || name.length === 0) continue;
    if (typeof url !== "string" || url.length === 0) continue;
    if (typeof iconUrl !== "string") continue;

    apps.push({ name, url, iconUrl });
  }

  return apps;
};

/**
 * Decodes the current JWT and returns the launcher apps the user is entitled to.
 *
 * The launcher backend (StoreFinancial OAuth) stamps one `launcherApp` claim per
 * entitled app into the token at login, already excluding the host app — so we
 * simply read and render the claim; no API call, no client-side self-exclusion.
 */
const useLauncherApps = (): LauncherApp[] => {
  const { token } = useAuth();

  return useMemo(() => {
    if (!token) return [];
    try {
      return parseLauncherClaim(jwtDecode<Jwt>(token)[CLAIM]);
    } catch {
      return [];
    }
  }, [token]);
};

export default useLauncherApps;
